'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useUser as useFirebaseUser, useAuth, useFirestore, type UserHookResult } from '@/firebase';
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { doc, type Firestore, onSnapshot } from 'firebase/firestore';
import { ROLE_EMAILS, UserRole } from '@/lib/roles';

interface AuthContextType extends UserHookResult {
  signIn: (email: string, pass: string) => Promise<any>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  changePassword: (currentPass: string, newPass: string) => Promise<void>;
  getRoleForUser: (email: string) => UserRole;
  userRole: UserRole | null;
  isProfileLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const userState = useFirebaseUser();
  const auth = useAuth();
  const firestore = useFirestore();
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);

  const getRoleForUser = (email: string): UserRole => {
      const e = email.toLowerCase();
      if (ROLE_EMAILS.ADMIN.some(mail => mail.toLowerCase() === e)) return 'ADMIN';
      if (ROLE_EMAILS.AVANZADO.some(mail => mail.toLowerCase() === e)) return 'AVANZADO';
      if (ROLE_EMAILS.GESTION.some(mail => mail.toLowerCase() === e)) return 'GESTION';
      if (ROLE_EMAILS.CONTRAPARTE.some(mail => mail.toLowerCase() === e)) return 'CONTRAPARTE';
      if (ROLE_EMAILS.OPERADOR.some(mail => mail.toLowerCase() === e)) return 'OPERADOR';
      if (ROLE_EMAILS.VISUALIZADOR.some(mail => mail.toLowerCase() === e)) return 'VISUALIZADOR';
      return 'OPERADOR';
  }

  useEffect(() => {
    let unsubscribe: () => void;

    const fetchRole = async () => {
        if (!userState.user) {
            setUserRole(null);
            setIsProfileLoading(false);
            return;
        }
        
        setIsProfileLoading(true);
        const email = userState.user.email;
        const uid = userState.user.uid;

        const roleByEmail = email ? getRoleForUser(email) : 'OPERADOR';
        
        // Protocolo de Acceso Rápido: Si es admin por email, entramos directo
        if (roleByEmail === 'ADMIN') {
            setUserRole('ADMIN');
            setIsProfileLoading(false);
            return;
        }

        // Timer de seguridad: Si Firestore no responde en 5s (ej. por cuota), permitimos acceso con rol base
        const safetyTimer = setTimeout(() => {
            if (isProfileLoading) {
                console.warn("Seguridad Industrial: Firestore lento o sin cuota. Usando rol por email.");
                setUserRole(roleByEmail);
                setIsProfileLoading(false);
            }
        }, 5000);

        const userDocRef = doc(firestore, 'users', uid);
        unsubscribe = onSnapshot(userDocRef, (docSnap) => {
            clearTimeout(safetyTimer);
            if (docSnap.exists()) {
                const data = docSnap.data();
                setUserRole(data.role as UserRole || roleByEmail);
            } else {
                setUserRole(roleByEmail);
            }
            setIsProfileLoading(false);
        }, (error) => {
            clearTimeout(safetyTimer);
            console.error("Error fetching user role:", error);
            setUserRole(roleByEmail);
            setIsProfileLoading(false);
        });
    };

    fetchRole();
    return () => {
        if (unsubscribe) unsubscribe();
    };
  }, [userState.user, firestore]);

  const signIn = (email: string, password: string) => signInWithEmailAndPassword(auth, email, password);
  const signOut = () => firebaseSignOut(auth);
  
  const resetPassword = async (email: string) => {
      if (!auth) throw new Error("Servicio de autenticación no disponible.");
      return sendPasswordResetEmail(auth, email);
  };

  const changePassword = async (currentPass: string, newPass: string) => {
      if (!userState.user || !userState.user.email) throw new Error("No hay una sesión activa.");
      const credential = EmailAuthProvider.credential(userState.user.email, currentPass);
      await reauthenticateWithCredential(userState.user, credential);
      await updatePassword(userState.user, newPass);
  };

  const value = {
    ...userState,
    signIn,
    signOut,
    resetPassword,
    changePassword,
    getRoleForUser,
    userRole,
    isProfileLoading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuthContext must be used within an AuthProvider');
  return context;
};

export { useAuthContext as useAuth };
